import { useCallback, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { createExternalRequestingDoctor } from '../services/AgendaService';
import { parseApiError } from '../types/error.types';
import { FieldError } from '../../../components/novarium/NvTableParts';

interface NewRequestingDoctorModalProps {
  visible: boolean;
  onHide: () => void;
  onCreated: (doctor: { id: number; name: string }) => void;
  onError?: (message: string) => void;
}

type Field = 'firstName' | 'lastName' | 'workerEnrollment';

const FIELD_IDS: Record<Field, string> = {
  firstName: 'requesting-doctor-first-name',
  lastName: 'requesting-doctor-last-name',
  workerEnrollment: 'requesting-doctor-enrollment',
};

const REQUIRED_MESSAGES: Record<Field, string> = {
  firstName: 'Completá el nombre',
  lastName: 'Completá el apellido',
  workerEnrollment: 'Completá la matrícula',
};

export default function NewRequestingDoctorModal({
  visible,
  onHide,
  onCreated,
  onError,
}: NewRequestingDoctorModalProps) {
  const [values, setValues] = useState<Record<Field, string>>({ firstName: '', lastName: '', workerEnrollment: '' });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const reset = useCallback(() => {
    setValues({ firstName: '', lastName: '', workerEnrollment: '' });
    setErrors({});
  }, []);

  const handleHide = useCallback(() => {
    if (saving) return;
    reset();
    onHide();
  }, [onHide, reset, saving]);

  const setValue = (field: Field, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    // La leyenda se borra apenas el valor queda bien
    if (value.trim()) setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSave = useCallback(async () => {
    // Obligatorios: marcador en cada campo que falta y foco en el primero
    const missing = (Object.keys(REQUIRED_MESSAGES) as Field[]).filter((field) => !values[field].trim());
    if (missing.length > 0) {
      setErrors(Object.fromEntries(missing.map((field) => [field, REQUIRED_MESSAGES[field]])));
      formRef.current?.querySelector<HTMLInputElement>(`#${FIELD_IDS[missing[0]]}`)?.focus();
      return;
    }

    setSaving(true);
    try {
      const doctor = await createExternalRequestingDoctor({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        workerEnrollment: values.workerEnrollment.trim(),
      });
      reset();
      onCreated(doctor);
      onHide();
    } catch (error) {
      onError?.(parseApiError(error, 'No se pudo crear el médico solicitante. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  }, [values, onCreated, onError, onHide, reset]);

  const field = (name: Field, label: string, widthClass: string, autoFocus = false) => (
    <div className={`nv-field ${widthClass}`}>
      <label htmlFor={FIELD_IDS[name]}>{label} <span className="nv-req">*</span></label>
      <InputText
        id={FIELD_IDS[name]}
        value={values[name]}
        onChange={(event) => setValue(name, event.target.value)}
        className={errors[name] ? 'p-invalid' : undefined}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${FIELD_IDS[name]}-error` : undefined}
        autoFocus={autoFocus}
      />
      <FieldError id={`${FIELD_IDS[name]}-error`} message={errors[name]} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header="Nuevo médico solicitante"
      modal
      draggable={false}
      className="nv-scope nv-dialog nv-dialog-s"
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={handleHide} disabled={saving} />
          <Button type="button" className="buttonMain" label="Guardar" onClick={handleSave} loading={saving} />
        </>
      )}
    >
      <div className="nv-dialog-body" ref={formRef}>
        {field('firstName', 'Nombre', 'nv-w-m', true)}
        {field('lastName', 'Apellido', 'nv-w-m')}
        {field('workerEnrollment', 'Matrícula', 'nv-w-s')}
      </div>
    </Dialog>
  );
}
